import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Spinner } from 'react-bootstrap';
import { ArrowLeft, CalendarDate, Person, JournalText, Facebook, Twitter, Instagram, Linkedin } from 'react-bootstrap-icons';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import SEOHead from './SEOHead';
import whiteLogo from '../assets/whitebglogo.png';
import androidQrCode from '../assets/playstoreqr.png';
import iosQrCode from '../assets/iosqrcode.png';
import '../App.css';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (!slug) return;
        const ref = doc(db, 'blogs', slug);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setPost({ id: snap.id, ...snap.data() });
        } else {
          setPost(null);
        }
      } catch (e) {
        console.error('Failed to load blog', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (!post) {
    if (loading) {
      return (
        <div className="modern-homepage">
          <section className="guide-header-section">
            <Container>
              <div className="text-center my-5">
                <Spinner animation="border" variant="primary" />
              </div>
            </Container>
          </section>
        </div>
      );
    }
    return (
      <div className="modern-homepage">
        <section className="guide-header-section">
          <Container>
            <Card className="guide-header-card">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="guide-badge d-inline-flex align-items-center gap-2">
                    <JournalText /> Blog
                  </span>
                  <h1 className="guide-title">Post not found</h1>
                  <p className="guide-subtitle">The article you are looking for doesn’t exist.</p>
                </div>
              </div>
              <div className="mt-3">
                <Button as={Link} to="/blog" variant="outline" className="class-back-btn">
                  <ArrowLeft className="me-2" /> Back to Blog
                </Button>
              </div>
            </Card>
          </Container>
        </section>
      </div>
    );
  }

  return (
    <div className="modern-homepage">
      <SEOHead
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        keywords={Array.isArray(post.seoKeywords) ? post.seoKeywords.join(', ') : ''}
        canonical={`/blog/${post.slug || post.id}`}
        ogImage={post.socialImage || '/favicon.ico'}
        ogType="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.seoDescription || post.excerpt,
          author: { '@type': 'Person', name: post.author || 'CBSE Resources Team' },
          datePublished: post.date?.seconds ? new Date(post.date.seconds * 1000).toISOString() : post.date,
          image: post.socialImage || post.image || '/favicon.ico',
        }}
      />
      <section className="guide-header-section">
        <Container>
          <Card className="guide-header-card">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
              <div>
                <span className="guide-badge d-inline-flex align-items-center gap-2">
                  <JournalText /> {post.tag || post.category}
                </span>
                <h1 className="guide-title">{post.title}</h1>
                <div className="guide-meta-info mt-2">
                  <div className="meta-item">
                    <CalendarDate className="me-2" /> {post.date && (post.date.seconds ? new Date(post.date.seconds * 1000).toLocaleDateString() : post.date)}
                  </div>
                  <div className="meta-item">
                    <Person className="me-2" /> {post.author}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <Button as={Link} to="/blog" variant="outline" className="class-back-btn">
                <ArrowLeft className="me-2" /> Back to Blog
              </Button>
            </div>
          </Card>
        </Container>
      </section>

      <section className="detailed-guide-section">
        <Container>
          <Card className="guide-content-card p-4">
            {Array.isArray(post.content) ? (
              post.content.map((paragraph, idx) => (
                <p key={idx} className="content-text">{paragraph}</p>
              ))
            ) : (
              <div className="content-text" dangerouslySetInnerHTML={{ __html: post.content }} />
            )}
          </Card>
        </Container>
      </section>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr-container">
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.cubegon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={androidQrCode} alt="Android QR code - Download from Play Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>Android</p>
                  </a>
                </div>
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://apps.apple.com/in/app/cubegon-smart-learning/id6752596017" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={iosQrCode} alt="iOS QR code - Download from App Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>iOS</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <div className="cubegon-footer-social">
              <a aria-label="Facebook" href="https://facebook.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Facebook />
              </a>
              <a aria-label="Twitter" href="https://twitter.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </a>
              <a aria-label="Instagram" href="https://instagram.com/cubegon.app" target="_blank" rel="noopener noreferrer">
                <Instagram />
              </a>
              <a aria-label="LinkedIn" href="https://linkedin.com/company/cubegon" target="_blank" rel="noopener noreferrer">
                <Linkedin />
              </a>
            </div>

            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="https://cubegon.app/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://cubegon.app/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;


